import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listPayments, resendPaymentCallback, type Payment } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { Webhook, Send, CheckCircle2 } from "lucide-react";

export function CallbackResendPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>("succeeded");
  const [resending, setResending] = useState<string | null>(null);
  const [resent, setResent] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listPayments({ limit: 100, status: status || undefined });
      setPayments(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onResend = async (p: Payment) => {
    if (!confirm(`Renvoyer la callback pour le paiement ${p.id} ?`)) return;
    setResending(p.id);
    setError(null);
    try {
      const r = await resendPaymentCallback(p.id);
      setResent((s) => ({ ...s, [p.id]: r.mode }));
    } catch (err: any) {
      setError(err?.detail || err?.message || "Erreur lors du renvoi");
    } finally {
      setResending(null);
    }
  };

  const columns: ColumnDef<Payment>[] = [
    {
      key: "id",
      header: "Payment ID",
      minWidth: 260,
      cell: (p) => <span className="font-mono text-xs text-[#7e8299]">{p.id}</span>,
    },
    {
      key: "reference",
      header: "Référence",
      minWidth: 200,
      cell: (p) => <span className="font-mono text-sm">{p.external_reference}</span>,
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 120,
      cell: (p) => <span className="font-semibold">{formatAmount(p.amount_minor, p.currency)}</span>,
    },
    {
      key: "method",
      header: "Méthode",
      minWidth: 110,
      cell: (p) => <Badge color="light">{p.payment_method}</Badge>,
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      cell: (p) => <Badge color={statusColor(p.status)}>{p.status}</Badge>,
    },
    {
      key: "date",
      header: "Date",
      minWidth: 130,
      cell: (p) => <span className="text-xs text-[#7e8299]">{formatDate(p.created_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      minWidth: 220,
      cell: (p) => (
        <div className="inline-flex items-center gap-2">
          {resent[p.id] && (
            <span className="inline-flex items-center gap-1 text-xs text-success-700">
              <CheckCircle2 size={12} />
              Renvoyé ({resent[p.id]})
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onResend(p);
            }}
            disabled={resending === p.id}
            className="btn btn-light-primary btn-sm"
          >
            <Send size={12} />
            {resending === p.id ? "Envoi…" : "Renvoyer"}
          </button>
        </div>
      ),
    },
  ];

  const filters = (
    <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
      <option value="">Tous statuts</option>
      <option value="succeeded">Succeeded</option>
      <option value="failed">Failed</option>
      <option value="processing">Processing</option>
      <option value="pending">Pending</option>
    </select>
  );

  return (
    <div>
      <PageHeader
        title="Renvoyer un callback"
        breadcrumbs={[{ label: "Transactions" }, { label: "Renvoyer callback" }]}
      />

      <div className="card mb-5 bg-brand-50 border border-brand-200">
        <div className="card-body py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-500 text-white flex items-center justify-center shrink-0">
            <Webhook size={18} />
          </div>
          <div className="text-sm text-[#3f4254] leading-relaxed">
            Si votre serveur n'a pas reçu (ou pas traité) le callback pour un paiement, vous pouvez le
            <strong> retransmettre manuellement</strong>. La tentative la plus récente sera reprogrammée
            (statut <code className="bg-white px-1 rounded">pending</code>, <code className="bg-white px-1 rounded">next_retry_at = now</code>).
            Le worker la pickera dans les 5 secondes.
          </div>
        </div>
      </div>

      {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

      <DataTable<Payment>
        title="Paiements"
        subtitle="Sélectionnez un paiement et cliquez Renvoyer"
        searchable
        searchPlaceholder="ID ou référence…"
        columns={columns}
        data={payments}
        rowKey={(p) => p.id}
        loading={loading}
        filtersSlot={filters}
        onRowClick={(p) => navigate(`/payments/${p.id}`)}
      />
    </div>
  );
}
